import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';

/*
  Badge — плашка для меток, статусов и счётчиков (R9).
  variant — тон (семантика, не цвет). solid — заливка тона + контрастный текст: обязательна для
  статусов и severity. Мягкая (по умолчанию) — для нейтральных меток и пометок в тексте.
  Фаза 3: API станет `tone` + `variant: solid | soft | outline`; сейчас — совместимый мост.
*/

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs leading-none font-medium',
  {
    variants: {
      variant: {
        neutral: '',
        coral: '',
        urgent: '',
        warn: '',
        success: '',
        info: '',
        ai: '',
      },
      solid: {
        true: 'border-transparent font-semibold',
        false: '',
      },
    },
    compoundVariants: [
      { variant: 'neutral', solid: false, className: 'border-line bg-muted text-fg-muted' },
      { variant: 'coral', solid: false, className: 'border-brand-fg/20 bg-brand-soft text-brand-fg' },
      { variant: 'urgent', solid: false, className: 'border-danger-line bg-danger-soft text-danger-strong' },
      { variant: 'warn', solid: false, className: 'border-warning-line bg-warning-soft text-warning-strong' },
      {
        variant: 'success',
        solid: false,
        className: 'border-success-line bg-success-soft text-success-strong',
      },
      { variant: 'info', solid: false, className: 'border-info-line bg-info-soft text-info-strong' },
      { variant: 'ai', solid: false, className: 'border-ai-line bg-ai-soft text-ai-strong' },
      { variant: 'neutral', solid: true, className: 'bg-fg-muted text-fg-inverse' },
      { variant: 'coral', solid: true, className: 'bg-brand-strong text-on-brand' },
      { variant: 'urgent', solid: true, className: 'bg-danger text-on-danger' },
      { variant: 'warn', solid: true, className: 'bg-warning text-on-warning' },
      { variant: 'success', solid: true, className: 'bg-success text-on-success' },
      { variant: 'info', solid: true, className: 'bg-info text-on-info' },
      { variant: 'ai', solid: true, className: 'bg-ai text-on-ai' },
    ],
    defaultVariants: { variant: 'neutral', solid: false },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

const dotColors: Record<BadgeVariant, string> = {
  neutral: 'bg-fg-subtle',
  coral: 'bg-brand',
  urgent: 'bg-danger',
  warn: 'bg-warning',
  success: 'bg-success',
  info: 'bg-info',
  ai: 'bg-ai',
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /** Заливка тона + контрастный текст — для статусов (R9). */
  solid?: boolean;
  children: ReactNode;
  /** dot=true — только цветная точка без фона (метки в строках списков). */
  dot?: boolean;
}

export function Badge({
  variant = 'neutral',
  solid = false,
  dot = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  if (dot) {
    return (
      <span data-slot="badge" className={cn('inline-flex items-center gap-1.5 text-xs', className)} {...rest}>
        <span className={cn('inline-block size-1.5 rounded-full', dotColors[variant])} />
        {children}
      </span>
    );
  }
  return (
    <span data-slot="badge" className={cn(badgeVariants({ variant, solid }), className)} {...rest}>
      {children}
    </span>
  );
}
